import { createApi } from '@helpers/api'
import { initI18n } from '@repo/i18n'
import type { Api, SettingsV1Firebase, SettingsV1Recaptcha } from '@sdk/Api'
import type { ErrorMessage, Upsertable } from '@store/errors'
import {
	createApp,
	createAuth,
	createFirestore,
	doGoogleLogin,
	doLogin,
} from '@trackdesk/firebase/client'
import type { User, UserCredential } from 'firebase/auth'
import { type Auth, signOut } from 'firebase/auth'
import type { Firestore } from 'firebase/firestore'

export let environmentDomain: string
export let recaptchaConfig: SettingsV1Recaptcha
export let auth: Auth
export let login: (
	email: string,
	password: string,
	errors?: Upsertable<ErrorMessage>,
) => Promise<void | UserCredential>
export let googleLogin: () => Promise<void | UserCredential>
export let logout: () => Promise<void>
export let firestore: Firestore
export let masterApi: Api<Auth>

export function initializeApp(
	environmentDomainSettings: string,
	firebaseSettings: SettingsV1Firebase,
	recaptchaSettings: SettingsV1Recaptcha,
) {
	environmentDomain = environmentDomainSettings
	recaptchaConfig = recaptchaSettings

	const firebaseApp = createApp(firebaseSettings.config)

	auth = createAuth(firebaseSettings.config, firebaseApp, firebaseSettings.authEmulatorHost)
	login = (email: string, password: string, errors?: Upsertable<ErrorMessage>) =>
		doLogin(auth, email, password, errors)
	googleLogin = () => doGoogleLogin(auth)
	logout = () => signOut(auth)

	firestore = createFirestore(
		firebaseSettings.config,
		firebaseApp,
		firebaseSettings.firestoreEmulatorHost,
	)

	masterApi = createApi(`https://my.${environmentDomain}/api/master`, auth)

	initI18n()
}

export function getAuthUser(): Promise<User | undefined> {
	return new Promise<User | undefined>((resolve) => {
		auth.onIdTokenChanged((user: User | null) => {
			if (!user) {
				resolve(undefined)
			} else {
				resolve(user)
			}
		})
	}).catch((e: unknown) => {
		throw e
	})
}
